import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { blogPosts } from "@/routes/blog";

export const Route = createFileRoute("/blog/")({
	component: BlogIndex,
});

function formatDate(date: string) {
	const parsed = new Date(date);
	if (Number.isNaN(parsed.getTime())) return date;
	return parsed.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

function BlogIndex() {
	const [featuredPost, ...posts] = blogPosts;

	return (
		<article className="space-y-10">
			<div className="space-y-3">
				<p className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground/70">
					BLOG
				</p>
				<h1 className="max-w-3xl text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
					Content creation guides for AI agents.
				</h1>
				<p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
					Short guides on MCP, generation patterns, model choice, and the
					workflows agents use to create finished images, video, audio, and
					campaign assets.
				</p>
			</div>

			{featuredPost ? (
				<Link
					to={`/blog/${featuredPost.slug}`}
					className="group grid overflow-hidden rounded-md border border-border bg-card transition-shadow hover:shadow-card md:grid-cols-[1fr_1.35fr]"
				>
					<div className="flex min-h-56 items-center justify-center border-b border-border bg-muted/40 p-8 md:border-r md:border-b-0">
						<div className="text-center">
							<p className="font-mono text-xs text-muted-foreground">
								FEATURED
							</p>
							<p className="mt-3 text-4xl font-semibold text-brand-accent">
								MCP
							</p>
						</div>
					</div>
					<div className="flex flex-col p-6 sm:p-8">
						<p className="text-xs text-muted-foreground/70">
							{formatDate(featuredPost.date)}
						</p>
						<h2 className="mt-3 text-2xl font-semibold tracking-[-0.03em] text-foreground transition-colors group-hover:text-primary sm:text-3xl">
							{featuredPost.title}
						</h2>
						<p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
							{featuredPost.description}
						</p>
						<span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-foreground">
							Read article
							<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
						</span>
					</div>
				</Link>
			) : null}

			<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
				{posts.map((post) => (
					<Link
						key={post.slug}
						to={`/blog/${post.slug}`}
						className="group flex min-h-56 flex-col rounded-md border border-border bg-card p-5 transition-shadow hover:shadow-card"
					>
						<p className="text-xs text-muted-foreground/70">
							{formatDate(post.date)}
						</p>
						<h2 className="mt-3 text-lg font-semibold tracking-[-0.02em] text-foreground transition-colors group-hover:text-primary">
							{post.title}
						</h2>
						<p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
							{post.description}
						</p>
						<span className="mt-auto pt-5 text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">
							Read more
						</span>
					</Link>
				))}
			</div>
		</article>
	);
}
